import type { HTMLAttributes, ReactNode } from "react";

import type { DropdownAvailableIconsEnum } from "@ryde/shared";

import { FramerTooltip } from "../../../common";
import { DropdownIconRenderer } from "./dropdown-icon-renderer";
import { resolveOptionTooltip } from "./dropdown-option-renderer.utils";
import { isHighlightedId } from "./highlighted-ids.utils";

export interface IDropdownOptionRendererParams {
  listItemProps: HTMLAttributes<HTMLLIElement>;
  option: string | number;
  getOptionLabel: (value: string | number) => string;
  boldValues?: (number | string)[];
  italicValues?: (number | string)[];
  disabledValues?: (number | string)[];
  optionIconMapping?: Map<string | number, DropdownAvailableIconsEnum>;
  /** Code name per option, shown on hover over a translated label. */
  technicalNames?: ReadonlyMap<unknown, string>;
  highlightedIdKeys?: ReadonlySet<string>;
  highlightedTooltip?: string;
}

export const DropdownOptionRenderer = ({
  listItemProps,
  option,
  getOptionLabel,
  boldValues,
  disabledValues,
  optionIconMapping,
  technicalNames,
  highlightedIdKeys,
  highlightedTooltip,
}: IDropdownOptionRendererParams): ReactNode => {
  const isBold = boldValues?.includes(option);
  const isDisabled = disabledValues?.includes(option);
  const isHighlighted =
    highlightedIdKeys !== undefined &&
    isHighlightedId({ option, highlightedIdKeys });
  const icon = DropdownIconRenderer(option, optionIconMapping);
  const technicalName = technicalNames?.get(option);
  const tooltip = resolveOptionTooltip({
    technicalName,
    isHighlighted,
    highlightedTooltip,
  });
  const label = getOptionLabel(option);

  return (
    <li
      {...listItemProps}
      key={option}
      style={{
        fontWeight: isBold || isHighlighted ? "bold" : "normal",
        color: isDisabled ? "gray" : "inherit",
        pointerEvents: isDisabled ? "none" : "auto",
        opacity: isDisabled ? 0.5 : 1,
      }}
    >
      {icon && <span style={{ marginInlineEnd: "8px" }}>{icon}</span>}
      {tooltip ? (
        <FramerTooltip content={tooltip}>
          <span>{label}</span>
        </FramerTooltip>
      ) : (
        label
      )}
    </li>
  );
};
